import React from 'react';

import type { TableGridView, TableView } from '@gitbook/doc-core';

import { useEditorWidth } from '../../EditorWidth';
import type { ColumnWidthsMap } from '../utils/contexts';
import { computeColumnWidths } from '../Views/utils/column-sizing';

interface TableLayoutResult {
    widths: ColumnWidthsMap;
    tableIsScrollable: boolean;
}

export const TABLE_COLUMN_SETTINGS_HEIGHT = 8;
export const TABLE_ROW_SETTINGS_WIDTH = 10;
export const TABLE_BORDER_WIDTH = 2;

/**
 * Hook calculate table layout properties such as widths and infer from that whether its scrolling.
 */
export function useTableLayout(view: TableView): TableLayoutResult {
    const rawWidth = useEditorWidth();

    const editorWidth = rawWidth - TABLE_BORDER_WIDTH - TABLE_ROW_SETTINGS_WIDTH; // Available width in-editor for table

    // Calculate column widths
    const computedWidths = React.useMemo(() => {
        if (view.type !== 'grid') {
            // Tables can also be card-view, in which case we don't need to calculate widths
            return { widths: new Map() as ColumnWidthsMap, totalWidth: 0 };
        }
        return computeColumnWidths(view as TableGridView, editorWidth);
    }, [editorWidth, view.columns, view]);

    // Determine if table is scrollable
    const tableIsScrollable = computedWidths.totalWidth > editorWidth;

    return {
        widths: computedWidths.widths,
        tableIsScrollable,
    };
}
